import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { format } from 'date-fns';

const fmtHour = (hour) => format(new Date(2000, 0, 1, hour), 'hh aa');

const fmtDate = (date) => {
  try {
    return date.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' });
  } catch {
    return '';
  }
};

/**
 * Confirmación previa a crear VARIAS sesiones: lista cada fecha con su horario y deja
 * elegir entre confirmar el lote completo o volver a editar las fechas.
 */
function SessionBatchConfirmDialog({ open, sessions, onConfirm, onClose }) {
  const { t } = useTranslation(['sessions', 'buttons']);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: 'surface.content',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main',
          color: 'text.primary',
        },
      }}>
      <DialogTitle sx={{ fontWeight: 800 }}>
        {t('confirmCreate', {
          ns: 'sessions',
          count: sessions.length,
          defaultValue: 'Crear {{count}} sesiones',
        })}
      </DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('confirmCreateHint', {
              ns: 'sessions',
              defaultValue: 'Revisa las fechas y horarios. Se creará una sesión por cada una.',
            })}
          </Typography>

          <Stack
            spacing={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'hairline.main',
              backgroundColor: 'surface.overlay',
              maxHeight: 260,
              overflowY: 'auto',
            }}>
            {sessions.map((s, index) => (
              <React.Fragment key={`${s.date.toISOString()}-${s.fromHour}`}>
                {index > 0 && <Divider sx={{ borderColor: 'hairline.main' }} />}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, py: 1 }}>
                  <EventOutlinedIcon sx={{ fontSize: 16, color: 'gold.main' }} />
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
                    {fmtDate(s.date)}
                    {s.trainerName && (
                      <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                        {' '}· {s.trainerName}
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {fmtHour(s.fromHour)} - {fmtHour(s.toHour)}
                  </Typography>
                </Stack>
              </React.Fragment>
            ))}
          </Stack>

          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              sx={{ py: 1.2, borderRadius: 2, fontWeight: 700, color: 'text.primary', borderColor: 'hairline.strong', '&:hover': { borderColor: 'text.disabled', background: 'rgba(255,255,255,0.06)' } }}>
              {t('keepEditing', { ns: 'sessions', defaultValue: 'Continuar editando' })}
            </Button>
            <Button
              fullWidth
              onClick={onConfirm}
              sx={{ py: 1.2, borderRadius: 2, fontWeight: 800, background: '#FFFFFF', color: '#0A0A0A', '&:hover': { background: '#FFFFFF', filter: 'brightness(0.94)' } }}>
              {t('confirm', { ns: 'buttons', defaultValue: 'Confirmar' })}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

SessionBatchConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  sessions: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.instanceOf(Date).isRequired,
      fromHour: PropTypes.number.isRequired,
      toHour: PropTypes.number.isRequired,
    })
  ).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SessionBatchConfirmDialog;
